import { useGetPresentersList } from '@/hooks';
import { useRevertAgent } from '@/pages/agents/api';
import { useCompareEditStates } from '@/pages/agents/hooks';
import { useAuth } from '@/services/auth';
import { useClearEditor } from '@/state/agent';
import { useAgentEditorStore } from '@/state/root';
import theme from '@/theme';
import { AppRoutes } from '@/utils/types';
import { AgentCreateTabType, PresenterUsecase } from '@d-id/types';
import styled from '@emotion/styled';
import { observer } from 'mobx-react-lite';
import { useNavigate } from 'react-router-dom';
import { needsToUpgradeForThisPresenter } from '../AgentAppearance/AgentAppearance';
import { HeaderButton } from '../common/HeaderButton';
import EditButton from '../EditButton';
import { CreateButton } from './CreateButton';

const HeaderButtonsContainer = styled.div<{ isAnonymous?: boolean }>`
    display: flex;
    flex-direction: row;
    justify-content: ${({ isAnonymous }) => (isAnonymous ? 'flex-end' : 'center')};
    align-items: center;

    gap: 16px;
`;

function getTabIndex(tab: AgentCreateTabType) {
    const index = Object.values(AgentCreateTabType).findIndex(_tab => _tab === tab);
    return index === -1 ? 0 : index;
}

export enum ButtonClickEvent {
    Back = 'Back',
    Next = 'Next',
}

export const HeaderButtonsWrapper = styled.div`
    border-top: 1px solid ${({ theme }) => theme.colors.black[50]};
    background: ${theme.colors.white};
`;

interface Props {
    onCancelChanges?: () => void;
    isEditAgent: boolean;
}

export const HeaderButtons = observer(({ onCancelChanges, isEditAgent }: Props) => {
    const agentEditorStore = useAgentEditorStore();

    const navigate = useNavigate();
    const { clearEditor } = useClearEditor();
    const { user, isAnonymous } = useAuth();
    const { getIsAgentStateChanged } = useCompareEditStates();
    const { mutateAsync: revertAgent } = useRevertAgent();
    const { data: presenters } = useGetPresentersList({ usecase: PresenterUsecase.Agent });

    const onLeave = async () => {
        if (isEditAgent) {
            await revertAgent();
        }

        clearEditor();
        navigate(AppRoutes.Agent);
    };

    const handleOnClick = (event: ButtonClickEvent) => {
        const presenter = presenters?.find(presenter => presenter.id === agentEditorStore.selectedPresenter);

        if (agentEditorStore.currentTab === AgentCreateTabType.Appearance && needsToUpgradeForThisPresenter(presenter, user, isAnonymous)) {
            agentEditorStore.setShowUpgradeModal(true);
            return;
        }

        let tabIndex = getTabIndex(agentEditorStore.currentTab);

        if (event === ButtonClickEvent.Back) {
            if (agentEditorStore.currentTab === AgentCreateTabType.Appearance) {
                if (isEditAgent && getIsAgentStateChanged()) {
                    onCancelChanges?.();
                } else {
                    return onLeave();
                }
            } else {
                tabIndex -= 1;
            }
        } else if (event === ButtonClickEvent.Next) {
            tabIndex += 1;
        }

        const tabType = Object.values(AgentCreateTabType).find((_type, i) => i === tabIndex) ?? AgentCreateTabType.Appearance;
        agentEditorStore.setCurrentTab(tabType);
    };

    const getButton = () => {
        if (isEditAgent) {
            return <EditButton />;
        } else if (agentEditorStore.currentTab === AgentCreateTabType.ChatSettings) {
            return <CreateButton />;
        } else {
            return (
                <HeaderButton variant="contained" color="secondary" onClick={() => handleOnClick(ButtonClickEvent.Next)} aria-label="Back">
                    Next
                </HeaderButton>
            );
        }
    };

    return (
        <HeaderButtonsContainer isAnonymous={isAnonymous}>
            <HeaderButton variant="outlined" color="secondary" onClick={() => handleOnClick(ButtonClickEvent.Back)} aria-label="Back">
                {agentEditorStore.currentTab === AgentCreateTabType.Appearance ? 'Cancel' : 'Back'}
            </HeaderButton>
            {getButton()}
        </HeaderButtonsContainer>
    );
});
